import { Download } from '@mui/icons-material'
import {
  CircularProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  type MenuProps,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import type { FileAttachment } from '@/features/search/types'

type Props = {
  mediaCode: number
  files: FileAttachment[]
} & MenuProps

export const DownloadMenu = (props: Props) => {
  const { mediaCode, files, ...menuProps } = props
  const items = useMemo(
    () =>
      files.map((file) => (
        <DownloadMenuItem key={file.code} mediaCode={mediaCode} file={file} />
      )),
    [files],
  )
  return <Menu {...menuProps}>{items}</Menu>
}

type ItemProps = {
  mediaCode: number
  file: FileAttachment
}
const DownloadMenuItem = (props: ItemProps) => {
  const [status, setStatus] = useState('initial')
  const [isLoading, setIsLoading] = useState(false)
  const baseUrl = import.meta.env.VITE_API_BASE
  const fileUrl = `${baseUrl}Search/Download/${props.mediaCode}/${props.file.name}`
  const { fetchWithAuth } = useAuth()

  const statusColorMap: Record<string, string | undefined> = {
    initial: undefined,
    success: 'success.main',
    error: 'error.main',
  }

  const handleClick = async () => {
    setIsLoading(true)

    try {
      await fetchWithAuth(fileUrl)
        .then((res) => {
          return res.blob()
        })
        .then((blob) => {
          const file = window.URL.createObjectURL(blob)
          //By creating an anchor element we can set the download name and initiate
          const link = document.createElement('a')
          link.href = file
          link.download = props.file.name
          link.click()
          setStatus('success')
          setTimeout(() => setStatus('initial'), 1000)
        })
    } catch (error) {
      console.error(error)
      setStatus('error')
      return false
    } finally {
      setIsLoading(false)
    }
  }
  return (
    <MenuItem
      sx={{ color: statusColorMap[status], width: '100%' }}
      onClick={handleClick}
      key={props.file.code}
    >
      <ListItemIcon>
        {isLoading ? (
          <CircularProgress size={24} thickness={6} />
        ) : (
          <Download />
        )}
      </ListItemIcon>
      {props.file.name}
    </MenuItem>
  )
}
